import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Checkbox,
  Box,
  alpha,
  useTheme
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';

interface FocusAreaSelectorProps {
  focusAreas: string[];
  onFocusAreaChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const FocusAreaSelector: React.FC<FocusAreaSelectorProps> = ({
  focusAreas,
  onFocusAreaChange
}) => {
  const theme = useTheme();

  const focusAreaConfig = [
    {
      id: 'financials',
      title: 'Financial Analysis',
      description: 'Revenue, costs, and profitability analysis',
      icon: '💰'
    },
    {
      id: 'market',
      title: 'Market Position',
      description: 'Competitive analysis and market positioning',
      icon: '📊'
    },
    {
      id: 'renovation',
      title: 'Property Renovation',
      description: 'Capital improvements and ROI assessment',
      icon: '🔨'
    },
    {
      id: 'sustainability',
      title: 'Sustainability',
      description: 'ESG factors and green initiatives',
      icon: '🌱'
    },
    {
      id: 'marketing',
      title: 'Marketing Strategy',
      description: 'Brand positioning and growth opportunities',
      icon: '📢'
    }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TuneIcon sx={{ color: theme.palette.primary.main }} />
        Focus Areas
      </Typography>

      <Grid container spacing={1.5}>
        {focusAreaConfig.map((area) => (
          <Grid item xs={12} sm={6} md={4} key={area.id}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                border: `1px solid ${focusAreas.includes(area.id) ? theme.palette.primary.main : alpha(theme.palette.divider, 0.3)}`,
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                bgcolor: focusAreas.includes(area.id) ? alpha(theme.palette.primary.main, 0.03) : 'transparent',
                minHeight: 110,
                position: 'relative',
                '&:hover': {
                  borderColor: focusAreas.includes(area.id) ? theme.palette.primary.dark : theme.palette.primary.main,
                  bgcolor: focusAreas.includes(area.id) ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.primary.main, 0.02),
                  transform: 'translateY(-1px)',
                  boxShadow: theme.shadows[2]
                }
              }}
              onClick={() => onFocusAreaChange({ target: { name: area.id, checked: !focusAreas.includes(area.id) } } as any)}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                      {area.icon}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      {area.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                    {area.description}
                  </Typography>
                </Box>
                <Checkbox
                  checked={focusAreas.includes(area.id)}
                  onChange={onFocusAreaChange}
                  name={area.id}
                  size="small"
                  sx={{
                    mt: -0.5,
                    mr: -0.5,
                    '&.Mui-checked': {
                      color: theme.palette.primary.main,
                    },
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </Box>

              {/* Selected indicator */}
              {focusAreas.includes(area.id) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main,
                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                  }}
                />
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default FocusAreaSelector;